import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, ShieldCheck } from "lucide-react";

import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { useStepUp } from "@/features/account/hooks/useStepUp";
import { useCeremonyOwner } from "@/hooks/useCeremonyOwner";
import { usePasskeys } from "@/features/account/hooks/usePasskeys";
import { StepUpDialog } from "@/features/account/components/StepUpDialog";
import { isStepUpRequired } from "@/features/account/api/stepup.api";
import { OpenContextDialog } from "@/features/staff/components/OpenContextDialog";
import {
  useAccountSearch,
  useOpenSupportContext,
  useSupportContext,
} from "@/features/staff/hooks/useStaff";
import { closeSupportContext } from "@/features/staff/api/staff.api";
import type { SupportAccountMatch } from "@/features/staff/api/staff.api";
import { Alert } from "@/components/ui/Alert";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { ApiError, isStaleSessionError } from "@/lib/api/client";
import { StaffOnly } from "@/features/staff/components/StaffOnly";

export const Route = createFileRoute("/_authenticated/staff/")({
  component: StaffSearchPage,
});

function StaffSearchPage() {
  return (
    <StaffOnly>
      <StaffSearch />
    </StaffOnly>
  );
}

function StaffSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pendingTarget, setPendingTarget] = useState<SupportAccountMatch | null>(null);
  const [failedClose, setFailedClose] = useState<{
    contextId: string;
    error: ApiError | Error;
  } | null>(null);
  const [closingFailed, setClosingFailed] = useState(false);
  const search = useAccountSearch();
  const openContext = useOpenSupportContext();
  const { setContext } = useSupportContext();
  const ceremonyOwner = useCeremonyOwner();
  const stepUp = useStepUp(ceremonyOwner);
  const passkeys = usePasskeys();
  const factorList = passkeys.isSuccess ? passkeys.data : undefined;
  const attemptRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      attemptRef.current += 1;
    };
  }, []);

  const matches = search.data?.accounts ?? [];

  const contextInFlight = (openContext.isPending && !openContext.isPaused) || stepUp.busy;

  const closeOrphanedContext = useCallback(
    (contextId: string) => {
      setClosingFailed(true);
      void closeSupportContext(contextId)
        .then(() => {
          if (!mountedRef.current) return;
          setFailedClose((prev) => (prev?.contextId === contextId ? null : prev));
        })
        .catch((error: unknown) => {
          if (!mountedRef.current) return;
          setFailedClose({
            contextId,
            error:
              error instanceof ApiError || error instanceof Error
                ? error
                : new Error(t("common.error")),
          });
        })
        .finally(() => {
          if (mountedRef.current) setClosingFailed(false);
        });
    },
    [t],
  );

  const closeContextDialog = () => {
    attemptRef.current += 1;
    setPendingTarget(null);
    stepUp.cancel();
    stepUp.forgetInFlight();
    openContext.reset();
  };

  const staleSession = isStaleSessionError(openContext.error);
  const contextDialogError = stepUp.dismissedRequired
    ? t("account.stepUp.dismissedIncomplete")
    : staleSession
      ? undefined
      : openContext.error && !isStepUpRequired(openContext.error)
        ? openContext.error.status === 429
          ? t("common.rateLimited")
          : (openContext.error.error ?? t("common.error"))
        : undefined;

  const confirmReason = (reason: string) => {
    const target = pendingTarget;
    if (!target) return;
    const attempt = ++attemptRef.current;
    void stepUp.run(
      (proof) =>
        openContext
          .mutateAsync({ targetAccountId: target.accountId, reason, stepUp: proof })
          .then((result) => {
            if (attemptRef.current !== attempt) {
              closeOrphanedContext(result.context.id);
              return;
            }
            setContext(result.context);
            setPendingTarget(null);
            void navigate({ to: "/staff/$accountId", params: { accountId: target.accountId } });
          }),
      { needsProof: true, dashboardOnly: true, onDismiss: openContext.reset },
    );
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex items-center gap-3">
        <ShieldCheck size={22} className="text-indigo" aria-hidden="true" />
        <div>
          <h1 className="text-2xl font-bold text-heading">{t("staff.title")}</h1>
          <p className="mt-1 text-sm text-muted">{t("staff.subtitle")}</p>
        </div>
      </div>

      <form
        className="mt-6 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (search.isPending) return;
          if (email.trim().length > 0) search.mutate(email.trim());
        }}
      >
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={t("staff.search.placeholder")}
          aria-label={t("staff.search.label")}
          readOnly={search.isPending}
          aria-disabled={search.isPending || undefined}
          className="flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-body aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!search.isPending && email.trim().length === 0}
          aria-disabled={search.isPending || undefined}
          aria-busy={search.isPending || undefined}
          data-autofocus={search.isPending ? true : undefined}
          className="flex items-center gap-2 rounded-lg bg-indigo px-4 py-2 text-sm font-medium text-white hover:bg-indigo-bright disabled:opacity-60 aria-disabled:opacity-60 aria-disabled:cursor-not-allowed"
        >
          <Search size={16} aria-hidden="true" />
          {search.isPending ? t("common.loading") : t("staff.search.submit")}
        </button>
      </form>

      {search.error && <ApiErrorBanner error={search.error} className="mt-4" />}

      {failedClose && (
        <div className="mt-4 space-y-3">
          <Alert variant="danger">
            <p>{t("staff.closeFailed.body")}</p>
            <p className="mt-1">
              {failedClose.error instanceof ApiError
                ? (failedClose.error.error ?? failedClose.error.message)
                : failedClose.error.message}
            </p>
          </Alert>
          <button
            type="button"
            onClick={() => closeOrphanedContext(failedClose.contextId)}
            disabled={closingFailed}
            className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted hover:bg-elevated hover:text-heading disabled:opacity-60"
          >
            {closingFailed ? t("staff.closeFailed.closing") : t("staff.banner.close")}
          </button>
        </div>
      )}

      {search.isSuccess && matches.length === 0 && (
        <p className="mt-6 text-sm text-muted">{t("staff.search.empty")}</p>
      )}

      {matches.length > 0 && (
        <ul className="mt-6 space-y-2">
          {matches.map((match) => (
            <li
              key={match.accountId}
              className="flex items-center justify-between rounded-lg border border-border bg-surface px-4 py-3"
            >
              <div>
                <p className="text-sm font-medium text-heading">{match.email}</p>
                <p className="text-xs text-muted">
                  {t("staff.search.summary", {
                    mfa: match.mfaEnabled ? t("staff.yes") : t("staff.no"),
                    verified: match.emailVerified ? t("staff.yes") : t("staff.no"),
                  })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPendingTarget(match)}
                className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted hover:bg-elevated hover:text-heading"
              >
                {t("staff.search.open")}
              </button>
            </li>
          ))}
        </ul>
      )}

      <OpenContextDialog
        key={pendingTarget?.accountId ?? "none"}
        open={pendingTarget !== null}
        busy={contextInFlight}
        targetEmail={pendingTarget?.email ?? ""}
        error={contextDialogError}
        staleSession={staleSession}
        staleSessionBody={t("account.staleSession.supportContextBody")}
        staleSessionReturnTo="/staff"
        onConfirm={confirmReason}
        onCancel={closeContextDialog}
      />

      <StepUpDialog
        key={stepUp.nonce}
        open={stepUp.open}
        busy={stepUp.busy}
        error={stepUp.error}
        proofOnWire={stepUp.proofOnWire}
        actionNeedsDashboardSession={stepUp.dashboardOnly}
        supportsWebAuthn={browserSupportsWebAuthn()}
        factorListUnavailable={factorList === undefined}
        hasPasskey={(factorList?.passkeys.length ?? 0) > 0}
        hasTotp={factorList?.totpEnabled ?? false}
        onPasskey={stepUp.withPasskey}
        onTotp={stepUp.withTotp}
        onCancel={stepUp.cancel}
        ceremonyOwner={ceremonyOwner}
        subtitle={t("account.stepUp.supportContextSubtitle")}
        staleSessionBody={t("account.staleSession.supportContextBody")}
        returnTo="/staff"
      />
    </div>
  );
}
